// HTTP client ky com interceptors de auth + observability — paralelo ao
// mobile (`grana-money-app/src/shared/api/client.ts`).
//
// Responsabilidades:
//   1. Injeta Authorization no request (beforeRequest).
//   2. Em 401, tenta refresh com mutex (N requests paralelas -> 1 refresh).
//   3. Em loop (401 apos retry), forca logout direto.
//   4. Push/pop de correlation-ID (X-Correlation-Id) e breadcrumb Sentry
//      por tentativa HTTP — para correlacao com logs do backend.
//
// Correlation-ID:
//   - push() apenas na 1a tentativa (retryCount === 0). Em retry, reusa o
//     ID atual via current() — toda a vida da request logica tem o mesmo ID.
//   - pop() em cada path que retorna response final (NAO no path de retry).
//   - Network errors / timeouts NAO chegam ao afterResponse — o stack pode
//     vazar 1 entrada por erro de rede. Aceitavel: stack ainda devolve o
//     ID via current(), e clear() em logout limpa estado de sessao.
//
// Mutex e codigo de seguranca: implementacao coberta por
// `src/shared/api/__tests__/client.test.ts`.
//
// Ky v2 usa hooks que recebem state object `{ request, options, response?, retryCount }`
// (NAO parametros posicionais). Retry usa `ky.retry({...})` — mecanismo
// canonico da v2 que reexecuta beforeRequest com token fresco.

import ky, { HTTPError } from 'ky';

import type { ApiResponse } from './client.types';

import { useAuthStore } from '@/shared/auth/auth.store';
import { tokenManager } from '@/shared/auth/token-manager';
import { ENV } from '@/shared/config/env';
import { addHttpBreadcrumb, logger } from '@/shared/lib/logger';
import { correlationId } from '@/shared/observability/correlation-id';

// Loop-guard: rastreia URLs ja retentadas apos refresh. Um 2o 401 para a
// mesma URL no mesmo ciclo NAO dispara novo refresh — forca logout. Map
// (em vez de Set) para purge por TTL — abandoned entries (request abortada
// por unmount, AbortSignal) sao limpas por TTL sem dependencia de
// afterResponse.
const retriedRequests = new Map<string, number>();
const RETRY_ENTRY_TTL_MS = 5 * 60 * 1000;

/**
 * Erro lancado quando o `/auth/refresh` recebe 429 (rate-limit). E TRANSITORIO
 * — o refresh token ainda e valido, so estamos limitados. NUNCA deve disparar
 * logout: quem chama (AuthProvider no boot, queries em voo) trata como
 * "tente de novo em instantes". Sem essa distincao, uma tempestade de refetch
 * que estoura o limiter deslogava o usuario no reload mesmo com "lembrar-me".
 */
export class RefreshRateLimitedError extends Error {
  constructor() {
    super('Token refresh rate-limited (429)');
    this.name = 'RefreshRateLimitedError';
  }
}

// Setado por `attemptTokenRefresh` quando a falha foi 429 (e nao um 401 / token
// invalido). Lido pelo interceptor logo apos `await refreshPromise` para
// decidir entre "lancar erro transitorio" (429) e "logout" (falha real).
let lastRefreshRateLimited = false;

// Default do ky e [408,413,429,500,502,503,504]. Removemos 429 — rate-limit
// nao deve ser retentado (evita amplificar e bater no limiter global).
const RETRY_STATUS_CODES = [408, 413, 500, 502, 503, 504];

function requestKey(request: Request): string {
  return `${request.method} ${request.url}`;
}

function purgeExpiredRetries(now: number = Date.now()): void {
  for (const [key, addedAt] of retriedRequests) {
    if (now - addedAt > RETRY_ENTRY_TTL_MS) {
      retriedRequests.delete(key);
    }
  }
}

// Mutex de modulo: N requests paralelas que recebem 401 compartilham 1
// promise — so o primeiro dispara `attemptTokenRefresh`; os outros dao
// `await` na mesma promise. Reset DEPOIS dos awaiters resolverem (no
// finally de attemptTokenRefresh).
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

export const api = ky.create({
  prefix: ENV.API_URL,
  timeout: 30_000,
  // Em teste (vitest, import.meta.env.MODE==='test') zeramos APENAS o backoff
  // (`delay: () => 0`), mantendo `limit: 2` — o fluxo de refresh em 401 (que
  // depende de ky.retry()) e a suite de mutex/loop-guard seguem validos, mas
  // os testes de erro (rede/5xx) afloram `isError` em ~ms em vez de ~900ms de
  // backoff, eliminando flake do `waitFor`/`findBy` (1000ms) sob carga.
  // Em build de prod `MODE` vira 'production' e o ramo de teste e eliminado.
  //
  // `statusCodes` EXCLUI 429 do default do ky ([408,413,429,500,502,503,504]):
  // retentar em rate-limit so amplifica o problema e aproxima o bloqueio do
  // limiter global. 429 deve borbulhar para a UI tratar (mensagem "muitas
  // requisicoes"), nao ser retentado automaticamente.
  retry:
    import.meta.env.MODE === 'test'
      ? { limit: 2, delay: () => 0, statusCodes: RETRY_STATUS_CODES }
      : { limit: 2, statusCodes: RETRY_STATUS_CODES },
  hooks: {
    beforeRequest: [
      ({ request, retryCount }) => {
        // Le do tokenManager (em memoria) — leitura sincrona, sem await
        // necessario. Se token nao existe, request segue sem Authorization
        // (ex: /auth/login que e publico).
        const token = tokenManager.getAccessToken();
        if (token) {
          request.headers.set('Authorization', `Bearer ${token}`);
        }

        // Correlation-ID: push apenas na 1a tentativa. Em retry, reusa ID
        // atual via current() — toda a vida da request logica tem o mesmo
        // ID (incluindo refresh+retry).
        const cid =
          retryCount === 0
            ? correlationId.push()
            : (correlationId.current() ?? correlationId.push());
        request.headers.set('X-Correlation-Id', cid);

        // Breadcrumb por tentativa HTTP (inclui retries — `retry: true`
        // diferencia visualmente). URL sem host/query: query pode conter
        // PII, host nao agrega informacao no dashboard.
        let pathOnly = request.url;
        try {
          pathOnly = new URL(request.url).pathname;
        } catch {
          // URL relativa em testes — manter raw.
        }
        addHttpBreadcrumb({
          method: request.method,
          url: pathOnly,
          correlation_id: cid,
          retry: retryCount > 0,
        });

        if (ENV.IS_DEV) {
          logger.api.debug(`${request.method} ${request.url}`);
        }
      },
    ],
    afterResponse: [
      async ({ request, response }) => {
        if (ENV.IS_DEV) {
          logger.api.debug(`${response.status} ${request.method} ${request.url}`);
        }

        purgeExpiredRetries();

        if (response.status !== 401) {
          retriedRequests.delete(requestKey(request));
          correlationId.pop();
          return response;
        }

        // Loop-guard: 2o 401 apos retry -> forca logout direto (algo quebrou:
        // token revogado, clock skew, tentativa de hijack).
        if (retriedRequests.has(requestKey(request))) {
          logger.auth.warn('401 apos retry — forcando logout');
          retriedRequests.delete(requestKey(request));
          useAuthStore.getState().logout();
          correlationId.pop();
          return response;
        }

        // Mutex: se ja esta refrescando, aguarda a mesma promise.
        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = attemptTokenRefresh();
        }

        const newToken = await refreshPromise;

        if (!newToken) {
          correlationId.pop();
          if (lastRefreshRateLimited) {
            // 429 no /auth/refresh: transitorio. NAO desloga — lanca erro
            // dedicado para o caller tratar (AuthProvider re-tenta com backoff;
            // outras queries afloram erro sem nukar a sessao).
            throw new RefreshRateLimitedError();
          }
          // Refresh falhou de verdade (sem refresh token, 401 no /auth/refresh,
          // shape invalido, rede). Forca logout e devolve o 401 original.
          useAuthStore.getState().logout();
          return response;
        }

        // Refresh OK. Marca esta URL+method (loop-guard) e dispara retry
        // via `ky.retry()` — mecanismo canonico v2 que reexecuta o ciclo
        // (beforeRetry, beforeRequest com retryCount incrementado).
        // Tambem injeta Bearer fresco explicitamente nos headers — alem
        // de beforeRequest reler do tokenManager.
        // NAO chamamos correlationId.pop() aqui — o ciclo logico continua
        // na retry; a proxima passada por afterResponse fecha.
        retriedRequests.set(requestKey(request), Date.now());
        const retryHeaders = new Headers(request.headers);
        retryHeaders.set('Authorization', `Bearer ${newToken}`);
        return ky.retry({
          request: new Request(request, { headers: retryHeaders }),
          code: 'TOKEN_REFRESHED',
        });
      },
    ],
  },
});

/**
 * Extrai access/refresh token do envelope de `POST /auth/refresh`, tolerando
 * os dois shapes que o backend pode emitir:
 *   - canonico (atual):  `{ tokens: { access_token, refresh_token } }`
 *   - legado (flat):     `{ access_token, refresh_token }`
 *
 * Retorna `null` se nenhum dos dois trouxer ambos os tokens como string.
 *
 * Por que tolerar o flat: o backend roda `replicas:2 + rolling update`, entao
 * durante um deploy a versao antiga (que retornava o shape flat, pre-commit
 * "fix auth") e a nova (aninhado) coexistem. Um refresh atendido pela replica
 * antiga voltaria flat e, sob o guard estrito anterior, deslogava o usuario
 * no reload — o sintoma classico de "lembrar-me nao funciona". Aceitar ambos
 * elimina essa classe de bug por version skew. O flat e LEGADO: quando todas
 * as replicas estiverem no shape canonico, o ramo flat simplesmente nunca
 * dispara (sem custo).
 */
export function extractRefreshTokens(
  data: unknown,
): { accessToken: string; refreshToken: string } | null {
  if (!data || typeof data !== 'object') return null;
  const root = data as Record<string, unknown>;
  // Prioriza o aninhado quando presente; so cai no flat se NAO houver objeto
  // `tokens` (envelope legado). Se `tokens` existir mas vier quebrado, e erro
  // real -> null -> logout (nao mascarar com fallback silencioso).
  const nested =
    root.tokens && typeof root.tokens === 'object'
      ? (root.tokens as Record<string, unknown>)
      : null;
  const candidate = nested ?? root;
  const accessToken = candidate.access_token;
  const refreshToken = candidate.refresh_token;
  if (typeof accessToken !== 'string' || typeof refreshToken !== 'string') {
    return null;
  }
  return { accessToken, refreshToken };
}

// Usa `ky` default (hookless) para NAO reentrar no interceptor 401. Se
// usasse `api` e o /auth/refresh retornasse 401, o afterResponse reentraria
// criando stack overflow.
async function attemptTokenRefresh(): Promise<string | null> {
  lastRefreshRateLimited = false;
  try {
    const refreshToken = tokenManager.getRefreshToken();
    if (!refreshToken) {
      return null;
    }

    const response = await ky
      .post(`${ENV.API_URL}/auth/refresh`, {
        json: { refresh_token: refreshToken },
        timeout: 10_000,
        retry: 0,
      })
      .json<ApiResponse<unknown>>();

    // Runtime shape guard — type diz success:true mas ky so faz cast, nao
    // valida shape. Extracao TOLERANTE a dois envelopes (ver
    // `extractRefreshTokens`): sem isso, um refresh atendido por uma versao
    // antiga do backend durante rolling deploy persistiria "undefined" no
    // storage e deslogaria o usuario.
    const tokens = extractRefreshTokens(response?.data);
    if (!tokens) {
      logger.auth.error('Refresh response com shape invalido — forcando logout');
      return null;
    }

    // setTokens ESCREVE antes da promise resolver — awaiters que chamam
    // tokenManager.getAccessToken() em beforeRequest leem o valor novo.
    tokenManager.setTokens(tokens.accessToken, tokens.refreshToken);
    return tokens.accessToken;
  } catch (err) {
    if (err instanceof HTTPError && err.response.status === 429) {
      // Rate-limit no refresh: transitorio. Sinaliza para o interceptor NAO
      // deslogar (ver `lastRefreshRateLimited`).
      lastRefreshRateLimited = true;
      logger.auth.warn('Token refresh rate-limited (429) — sessao preservada');
    } else {
      logger.auth.error('Token refresh failed:', err);
    }
    return null;
  } finally {
    // Reset so aqui — NAO no afterResponse. Garante que o ultimo awaiter
    // terminou antes de abrir nova janela de refresh.
    isRefreshing = false;
    refreshPromise = null;
  }
}

// Apenas para testes — acesso ao Map interno para verificar shape do
// loop-guard e cobertura de TTL expiracao. NAO usar em producao.
export const __testOnly__ = {
  retriedRequests,
  RETRY_ENTRY_TTL_MS,
  purgeExpiredRetries,
  resetRefreshState(): void {
    isRefreshing = false;
    refreshPromise = null;
    retriedRequests.clear();
  },
};
